/**
 * CTRL_03 Plugin - Infrastructure Event Filtering
 * Custom UI with filtered events dashboard and keyword management
 * Extends GenericConfigPanel with visual filter statistics
 */

import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Statistic,
  Alert,
  Table,
  Tag,
  Space,
  Badge,
} from 'antd';
import {
  FilterOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from '@ant-design/icons';
import GenericConfigPanel from '../GenericConfigPanel';
import { colors } from '../../../../theme/designTokens';

/**
 * Infrastructure Filter Dashboard Component
 */
function InfrastructureFilterDashboard({ config, projectId }) {
  const [stats, setStats] = useState({
    total: 0,
    filtered: 0,
    passed: 0,
  });
  const [filteredEvents, setFilteredEvents] = useState([]);

  useEffect(() => {
    if (config?.enabled && projectId) {
      fetchFilterStats();
    }
  }, [config, projectId]);

  const fetchFilterStats = async () => {
    // Mock data - replace with actual API call
    setTimeout(() => {
      setStats({
        total: 1250,
        filtered: 385,
        passed: 865,
      });
      setFilteredEvents([
        {
          id: 1,
          event_type: 'deployment',
          matched_keyword: 'kubernetes',
          timestamp: new Date().toISOString(),
        },
        {
          id: 2,
          event_type: 'build',
          matched_keyword: 'jenkins',
          timestamp: new Date().toISOString(),
        },
      ]);
    }, 500);
  };

  const columns = [
    {
      title: 'Event Type',
      dataIndex: 'event_type',
      key: 'event_type',
      render: (type) => <Tag>{type}</Tag>,
    },
    {
      title: 'Matched Keyword',
      dataIndex: 'matched_keyword',
      key: 'matched_keyword',
      render: (keyword) => <Tag color="blue">{keyword}</Tag>,
    },
    {
      title: 'Timestamp',
      dataIndex: 'timestamp',
      key: 'timestamp',
      render: (ts) => new Date(ts).toLocaleString(),
    },
  ];

  if (!config?.enabled) {
    return (
      <Alert
        message="Infrastructure Filtering Disabled"
        description="Enable filtering to automatically exclude infrastructure events."
        type="info"
        showIcon
      />
    );
  }

  const filterRate = stats.total > 0 ? ((stats.filtered / stats.total) * 100).toFixed(1) : 0;

  return (
    <Space direction="vertical" size="large" style={{ width: '100%' }}>
      {/* Filter Statistics */}
      <Row gutter={16}>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Total Events"
              value={stats.total}
              prefix={<FilterOutlined />}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Filtered Out"
              value={stats.filtered}
              prefix={<CloseCircleOutlined />}
              valueStyle={{ color: colors.status.warning }}
              suffix={`(${filterRate}%)`}
            />
          </Card>
        </Col>
        <Col span={8}>
          <Card size="small">
            <Statistic
              title="Passed Through"
              value={stats.passed}
              prefix={<CheckCircleOutlined />}
              valueStyle={{ color: colors.status.pass }}
            />
          </Card>
        </Col>
      </Row>

      {/* Recently Filtered Events */}
      {filteredEvents.length > 0 && (
        <div>
          <h4>Recently Filtered Events</h4>
          <Table
            columns={columns}
            dataSource={filteredEvents}
            rowKey="id"
            size="small"
            pagination={{ pageSize: 5 }}
          />
        </div>
      )}

      {/* Active Keywords */}
      <Card size="small" title="Active Keywords">
        <Space wrap>
          {config?.infrastructure_keywords?.map((keyword) => (
            <Badge key={keyword} count="active" color={colors.primary[500]}>
              <Tag>{keyword}</Tag>
            </Badge>
          ))}
        </Space>
      </Card>
    </Space>
  );
}

/**
 * CTRL_03 Plugin Component
 */
function CTRL_03_Plugin({ projectId, controlCode }) {
  const [config, setConfig] = useState({
    enabled: false,
    filter_strategy: 'allowlist',
    infrastructure_keywords: ['kubernetes', 'docker', 'jenkins'],
  });

  return (
    <div>
      {/* Filter Dashboard */}
      <InfrastructureFilterDashboard config={config} projectId={projectId} />

      {/* Generic Config Panel - usePlugin={false} prevents infinite loop */}
      <GenericConfigPanel projectId={projectId} controlCode={controlCode} usePlugin={false} />
    </div>
  );
}

export default CTRL_03_Plugin;
